import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

const SEO = ({ titleKey, descriptionKey, image, noindex = false }) => {
    const { t, i18n } = useTranslation();
    const location = useLocation();
    const domain = 'https://mycoachserver.com';
    const fullImageUrl = image ? `${domain}${image}` : `${domain}/images/seo/default-share.jpg`;

    // Calculate clean path (without language prefix) to generate alternate links
    const currentPath = location.pathname;
    const parts = currentPath.split('/');
    const supported = ['en', 'pt', 'es', 'fr', 'it', 'de'];

    let cleanPath = currentPath;
    let currentLangPrefix = 'en'; // default

    if (parts.length > 1 && supported.includes(parts[1])) {
        currentLangPrefix = parts[1];
        cleanPath = '/' + parts.slice(2).join('/');
    }

    if (cleanPath === '//') cleanPath = '/';

    const canonicalUrl = `${domain}/${currentLangPrefix}${cleanPath === '/' ? '' : cleanPath}`;

    // Sport image map — fallback to actual images that exist in /public/images
    const sportImageMap = {
        football: `${domain}/images/footballMyCoachServer.jpg`,
        futsal: `${domain}/images/futsalMyCoachServer.jpg`,
        handball: `${domain}/images/handballMyCoachServer.jpg`,
        volleyball: `${domain}/images/voleyballMyCoachServer.jpg`,
        basketball: `${domain}/images/basketballMyCoachServer.jpg`,
    };

    return (
        <Helmet>
            <title>{t(titleKey)}</title>
            <meta name="description" content={t(descriptionKey)} />

            {/* Noindex for private pages — keep crawlers away */}
            {noindex && <meta name="robots" content="noindex,nofollow" />}

            {/* Open Graph / Facebook / WhatsApp */}
            <meta property="og:type" content="website" />
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:title" content={t(titleKey)} />
            <meta property="og:description" content={t(descriptionKey)} />
            <meta property="og:image" content={fullImageUrl} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:locale" content={i18n.language} />

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:url" content={canonicalUrl} />
            <meta name="twitter:title" content={t(titleKey)} />
            <meta name="twitter:description" content={t(descriptionKey)} />
            <meta name="twitter:image" content={fullImageUrl} />

            {/* Canonical URL — only on indexable pages */}
            {!noindex && <link rel="canonical" href={canonicalUrl} />}

            {/* Hreflang — only on indexable pages */}
            {!noindex && supported.map(lang => (
                <link
                    key={lang}
                    rel="alternate"
                    hreflang={lang}
                    href={`${domain}/${lang}${cleanPath === '/' ? '' : cleanPath}`}
                />
            ))}
            {!noindex && <link rel="alternate" hreflang="x-default" href={`${domain}/en${cleanPath === '/' ? '' : cleanPath}`} />}

            {/* Schema.org — only on indexable pages */}
            {!noindex && (
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "SoftwareApplication",
                        "name": "MyCoachServer",
                        "url": canonicalUrl,
                        "image": fullImageUrl,
                        "description": t(descriptionKey),
                        "applicationCategory": "SportsApplication",
                        "operatingSystem": "Web Browser, Windows, macOS, Android, iOS",
                        "offers": [
                            {
                                "@type": "Offer",
                                "name": "Free Plan",
                                "price": "0",
                                "priceCurrency": "EUR",
                                "availability": "https://schema.org/InStock",
                                "url": `${domain}/en/pricing`
                            },
                            {
                                "@type": "Offer",
                                "name": "Premium Plan",
                                "price": "20",
                                "priceCurrency": "EUR",
                                "availability": "https://schema.org/InStock",
                                "priceSpecification": {
                                    "@type": "UnitPriceSpecification",
                                    "price": "20",
                                    "priceCurrency": "EUR",
                                    "unitCode": "MON",
                                    "billingIncrement": 1
                                },
                                "url": `${domain}/en/pricing`
                            }
                        ],
                        "author": {
                            "@type": "Organization",
                            "name": "MyCoachServer Team",
                            "url": domain
                        }
                    })}
                </script>
            )}
        </Helmet>
    );
};

export default SEO;
